/**
 * Tamper-Evident Audit Logging Service for Vedanga Jyotisha
 * Records security-sensitive operations, backups, exports, and DLP triggers.
 */

export type AuditEventType =
  | 'ROLE_CHANGE'
  | 'DATA_EXPORT'
  | 'BACKUP_CREATED'
  | 'BACKUP_RESTORED'
  | 'PROFILE_DELETED'
  | 'KUNDALI_DELETED'
  | 'DLP_TRIGGERED'
  | 'AI_TOOL_CALLED'
  | 'REGRESSION_SUITE_RUN'
  | 'SECURITY_CHECK';

export interface AuditEvent {
  eventId: string;
  timestamp: string;
  eventType: AuditEventType;
  userId: string;
  userEmail: string;
  userRole: string;
  resourceId?: string;
  details: string;
  integrityHash: string;
}

const AUDIT_STORAGE_KEY = 'vedanga_audit_trail_log';

/**
 * Computes a hash string for tamper evidence
 */
function createEventHash(timestamp: string, eventType: string, userId: string, details: string): string {
  let str = `${timestamp}_${eventType}_${userId}_${details}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return `EVH_${Math.abs(hash).toString(16)}`;
}

/**
 * Appends a new audit event
 */
export function logAuditEvent(
  eventType: AuditEventType,
  user: { uid?: string; email?: string; role?: string } | null,
  details: string,
  resourceId?: string
): AuditEvent {
  const timestamp = new Date().toISOString();
  const userId = user?.uid || 'anonymous';
  const userEmail = user?.email || 'anonymous@offline';
  const userRole = user?.role || 'user';
  
  const eventId = `AUD_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
  const integrityHash = createEventHash(timestamp, eventType, userId, details);

  const event: AuditEvent = {
    eventId,
    timestamp,
    eventType,
    userId,
    userEmail,
    userRole,
    resourceId,
    details,
    integrityHash
  };

  try {
    const existingRaw = localStorage.getItem(AUDIT_STORAGE_KEY);
    const logList: AuditEvent[] = existingRaw ? JSON.parse(existingRaw) : [];
    logList.unshift(event); // Newest first
    
    // Maintain rolling buffer of 200 events in local storage
    if (logList.length > 200) logList.length = 200;
    
    localStorage.setItem(AUDIT_STORAGE_KEY, JSON.stringify(logList));
  } catch (err) {
    console.warn('Audit logging storage notice:', err);
  }

  // Asynchronously relay to server audit log endpoint if available
  try {
    if (typeof fetch !== 'undefined') {
      fetch('/api/audit-logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId: event.eventId,
          timestamp: event.timestamp,
          eventType: event.eventType,
          userId: event.userId,
          userEmail: event.userEmail,
          userRole: event.userRole,
          resourceId: event.resourceId,
          details: event.details,
          decision: 'ALLOW',
          action: event.eventType
        })
      }).catch(() => {});
    }
  } catch {}

  return event;
}

/**
 * Retrieves the local audit events
 */
export function getAuditEvents(): AuditEvent[] {
  try {
    const existingRaw = localStorage.getItem(AUDIT_STORAGE_KEY);
    return existingRaw ? JSON.parse(existingRaw) : [];
  } catch {
    return [];
  }
}

/**
 * Exports audit trail as JSON
 */
export function exportAuditLogs(): string {
  const logs = getAuditEvents();
  return JSON.stringify(logs, null, 2);
}
